import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, CircularProgress, Alert, Chip, Paper,
  Select, MenuItem, FormControl, InputLabel, IconButton,
  Tooltip, Collapse, Divider, LinearProgress,
} from '@mui/material';
import {
  Memory as CorePlatformIcon,
  Hub as ThirdPartyIcon,
  Visibility as ObservabilityIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as DoneIcon,
  PlayCircle as ActiveIcon,
  Schedule as NewIcon,
  FolderOpen as InitiativeIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { get } from '../../services/apiService';

// ── Design tokens ─────────────────────────────────────────────────────────────
const PRIMARY     = '#12365a';
const MUTED       = '#6d788c';

const PILLAR_META = {
  corePlatform:  { label: 'Core Platform & Runtime Health',      icon: CorePlatformIcon,  color: '#1d6fd4' },
  thirdParty:    { label: 'Third-Party Integration Reliability',  icon: ThirdPartyIcon,    color: '#e53935' },
  observability: { label: 'Safe Delivery',                        icon: ObservabilityIcon, color: '#f4511e' },
};

const QUARTERS = [
  { key: 'all',     label: 'All' },
  { key: 'Q3-2026', label: 'Q3 2026', color: '#e65100' },
  { key: 'Q4-2026', label: 'Q4 2026', color: '#6a1b9a' },
];

const DONE_STATES   = new Set(['Closed', 'Done', 'Completed']);
const ACTIVE_STATES = new Set(['Active', 'In Progress', 'Committed']);

const RELIABILITY_CAT_PREFIX = 'reliability-category-';
const QUARTER_TAGS = new Set(['q3-2026', 'q4-2026']);

// ── Helpers ──────────────────────────────────────────────────────────────────

function getStateInfo(state) {
  if (DONE_STATES.has(state))   return { label: state, color: '#2e7d32', bg: '#e8f5e9', icon: <DoneIcon  sx={{ fontSize: 12 }} /> };
  if (ACTIVE_STATES.has(state)) return { label: state, color: '#1565c0', bg: '#e3f2fd', icon: <ActiveIcon sx={{ fontSize: 12 }} /> };
  return                               { label: state || 'New', color: '#795548', bg: '#efebe9', icon: <NewIcon    sx={{ fontSize: 12 }} /> };
}

function crColor(v) {
  if (v >= 100) return '#2e7d32';
  if (v >= 67)  return '#43a047';
  if (v >= 33)  return '#fb8c00';
  return '#e53935';
}

function featureQuarterTags(tags) {
  return (tags || []).filter(t => QUARTER_TAGS.has(t.toLowerCase()));
}

function featureInitiative(tags, subCatMap) {
  for (const t of (tags || [])) {
    if (t.toLowerCase().startsWith(RELIABILITY_CAT_PREFIX)) {
      const alias = t.slice(RELIABILITY_CAT_PREFIX.length);
      return subCatMap[alias] || null;
    }
  }
  return null;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function QuarterChip({ label, color }) {
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: 18, fontSize: '0.67rem', fontWeight: 700,
        bgcolor: `${color}18`, color, borderRadius: '5px',
        '& .MuiChip-label': { px: 0.75 },
      }}
    />
  );
}

const PRIORITY_META = {
  1: { label: 'P1', color: '#c62828', bg: '#ffebee' },
  2: { label: 'P2', color: '#e65100', bg: '#fff3e0' },
  3: { label: 'P3', color: '#f57f17', bg: '#fffde7' },
  4: { label: 'P4', color: '#546e7a', bg: '#eceff1' },
};

const PriorityBadge = ({ value }) => {
  const meta = PRIORITY_META[value];
  if (!meta) return null;
  return (
    <Tooltip title={`Priority ${value}`}>
      <Box sx={{
        px: 0.75, py: 0.15, borderRadius: '5px',
        bgcolor: meta.bg, display: 'inline-flex', alignItems: 'center', flexShrink: 0
      }}>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: meta.color, lineHeight: 1.4 }}>
          {meta.label}
        </Typography>
      </Box>
    </Tooltip>
  );
};

function ProgressBar({ value, color }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
      <Box sx={{ flex: 1, height: 5, borderRadius: 3, bgcolor: 'rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <Box sx={{ width: `${Math.min(value, 100)}%`, height: '100%', bgcolor: color, borderRadius: 3, transition: 'width 0.4s ease' }} />
      </Box>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color, minWidth: 30, textAlign: 'right' }}>
        {value}%
      </Typography>
    </Box>
  );
}

function FeatureRow({ feature, subCatMap }) {
  const stateInfo   = getStateInfo(feature.state);
  const quarterTags = featureQuarterTags(feature.tags);
  const initiative  = featureInitiative(feature.tags, subCatMap);
  const color       = crColor(feature.completionRate);

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5,
      px: 2.5, py: 1.25,
      '&:hover': { bgcolor: 'rgba(0,0,0,0.018)' },
      transition: 'background 0.12s',
    }}>
      {/* State dot */}
      <Box sx={{
        width: 8, height: 8, borderRadius: '50%',
        bgcolor: stateInfo.color, flexShrink: 0, mt: 0.1,
      }} />

      {/* Title + initiative */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          noWrap
          sx={{ fontSize: '0.83rem', fontWeight: 500, color: '#1a1a1a', lineHeight: 1.35 }}
          title={feature.title}
        >
          {feature.title}
        </Typography>
        {initiative && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.25 }}>
            <InitiativeIcon sx={{ fontSize: 11, color: MUTED }} />
            <Typography sx={{ fontSize: '0.7rem', color: MUTED }}>{initiative}</Typography>
          </Box>
        )}
      </Box>

      {/* Progress */}
      <Box sx={{ minWidth: 130, flexShrink: 0 }}>
        <ProgressBar value={feature.completionRate} color={color} />
      </Box>

      {/* Story points */}
      {feature.storyPoints > 0 && (
        <Tooltip title="Story Points">
          <Box sx={{
            px: 0.9, py: 0.2, borderRadius: '5px',
            bgcolor: 'rgba(0,0,0,0.06)', flexShrink: 0,
            display: 'flex', alignItems: 'center', gap: 0.25,
          }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#333' }}>
              {feature.storyPoints}
            </Typography>
            <Typography sx={{ fontSize: '0.62rem', color: MUTED }}>sp</Typography>
          </Box>
        </Tooltip>
      )}

      {/* Fix version */}
      {feature.fixVersion && (
        <Tooltip title="Fix Version">
          <Chip
            label={feature.fixVersion}
            size="small"
            sx={{
              height: 18, fontSize: '0.67rem', fontWeight: 600,
              bgcolor: 'rgba(18,54,90,0.08)', color: PRIMARY, borderRadius: '5px',
              '& .MuiChip-label': { px: 0.75 }, flexShrink: 0,
            }}
          />
        </Tooltip>
      )}

      {/* Quarter tags */}
      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
        {quarterTags.length > 0
          ? quarterTags.map(qt => {
              const qmeta = QUARTERS.find(q => q.key.toLowerCase() === qt.toLowerCase());
              return <QuarterChip key={qt} label={qmeta ? qmeta.label : qt} color={qmeta?.color || MUTED} />;
            })
          : <Chip label="Unscheduled" size="small" sx={{ height: 18, fontSize: '0.67rem', color: MUTED, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: '5px', '& .MuiChip-label': { px: 0.75 } }} />
        }
      </Box>

      {/* State chip */}
      <Tooltip title={`State: ${stateInfo.label}`}>
        <Box sx={{
          px: 0.8, py: 0.2, borderRadius: '5px', bgcolor: stateInfo.bg,
          display: 'flex', alignItems: 'center', gap: 0.35, flexShrink: 0,
        }}>
          <Box sx={{ color: stateInfo.color, display: 'flex', alignItems: 'center' }}>{stateInfo.icon}</Box>
          <Typography sx={{ fontSize: '0.67rem', fontWeight: 600, color: stateInfo.color }}>
            {stateInfo.label}
          </Typography>
        </Box>
      </Tooltip>

      {/* Priority */}
      <PriorityBadge value={feature.priority} />

      {/* ADO link */}
      {feature.adoUrl && (
        <Tooltip title="Open in Azure DevOps">
          <IconButton
            size="small"
            component="a"
            href={feature.adoUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            sx={{ color: MUTED, '&:hover': { color: PRIMARY }, p: 0.4, flexShrink: 0 }}
          >
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

function PillarSection({ pillarKey, features, subCatMap, selectedQuarter, fixVersionFilter, priorityFilter }) {
  const [expanded, setExpanded] = useState(true);
  const meta = PILLAR_META[pillarKey];

  const filtered = useMemo(() => {
    if (!meta) return [];
    let result = features;
    if (selectedQuarter !== 'all') {
      result = result.filter(f =>
        (f.tags || []).some(t => t.toLowerCase() === selectedQuarter.toLowerCase())
      );
    }
    if (fixVersionFilter) {
      result = result.filter(f => f.fixVersion === fixVersionFilter);
    }
    if (priorityFilter !== 'all') {
      result = result.filter(f => String(f.priority) === priorityFilter);
    }
    return result;
  }, [meta, features, selectedQuarter, fixVersionFilter, priorityFilter]);

  if (!meta) return null;

  const totalSP   = filtered.reduce((s, f) => s + (f.storyPoints || 0), 0);
  const doneCt    = filtered.filter(f => DONE_STATES.has(f.state)).length;
  const activeCt  = filtered.filter(f => ACTIVE_STATES.has(f.state)).length;
  const avgCR     = filtered.length
    ? Math.round(filtered.reduce((s, f) => s + (f.completionRate || 0), 0) / filtered.length)
    : 0;

  const Icon = meta.icon;

  return (
    <Paper elevation={0} sx={{ borderRadius: '14px', overflow: 'hidden', border: '1.5px solid rgba(0,0,0,0.08)', mb: 2 }}>
      {/* Pillar header */}
      <Box
        onClick={() => setExpanded(p => !p)}
        sx={{
          px: 2.5, py: 1.75,
          bgcolor: `${meta.color}0c`,
          display: 'flex', alignItems: 'center', gap: 1.5,
          cursor: 'pointer', userSelect: 'none',
          '&:hover': { bgcolor: `${meta.color}14` },
          transition: 'background 0.12s',
        }}
      >
        <Box sx={{
          width: 36, height: 36, borderRadius: '9px',
          bgcolor: `${meta.color}1c`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon sx={{ color: meta.color, fontSize: 19 }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 650, fontSize: '0.92rem', color: '#1a1a1a' }}>
            {meta.label}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, mt: 0.25, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '0.72rem', color: MUTED }}>
              {filtered.length} feature{filtered.length !== 1 ? 's' : ''}
            </Typography>
            {totalSP > 0 && (
              <Typography sx={{ fontSize: '0.72rem', color: MUTED }}>{totalSP} SP</Typography>
            )}
            {doneCt > 0 && (
              <Typography sx={{ fontSize: '0.72rem', color: '#2e7d32', fontWeight: 600 }}>
                {doneCt} done
              </Typography>
            )}
            {activeCt > 0 && (
              <Typography sx={{ fontSize: '0.72rem', color: '#1565c0', fontWeight: 600 }}>
                {activeCt} active
              </Typography>
            )}
          </Box>
        </Box>

        {/* Pillar avg completion mini bar */}
        {filtered.length > 0 && (
          <Box sx={{ minWidth: 110, flexShrink: 0 }}>
            <ProgressBar value={avgCR} color={crColor(avgCR)} />
          </Box>
        )}

        <IconButton size="small" sx={{ color: MUTED, p: 0.5 }}>
          {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        {filtered.length === 0 ? (
          <Box sx={{ px: 3, py: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem' }}>
              {selectedQuarter !== 'all'
                ? `No features tagged ${selectedQuarter} in this pillar.`
                : 'No features in this pillar.'}
            </Typography>
          </Box>
        ) : (
          <Box>
            {filtered.map((feature, idx) => (
              <React.Fragment key={feature.id}>
                {idx > 0 && <Divider sx={{ mx: 2 }} />}
                <FeatureRow feature={feature} subCatMap={subCatMap} />
              </React.Fragment>
            ))}
          </Box>
        )}
      </Collapse>
    </Paper>
  );
}

// ── Stats banner ──────────────────────────────────────────────────────────────

function StatBanner({ features, selectedQuarter, fixVersionFilter, priorityFilter }) {
  const visible = useMemo(() => {
    let f = features;
    if (selectedQuarter !== 'all') {
      f = f.filter(feat => (feat.tags || []).some(t => t.toLowerCase() === selectedQuarter.toLowerCase()));
    }
    if (fixVersionFilter) {
      f = f.filter(feat => feat.fixVersion === fixVersionFilter);
    }
    if (priorityFilter !== 'all') {
      f = f.filter(feat => String(feat.priority) === priorityFilter);
    }
    return f;
  }, [features, selectedQuarter, fixVersionFilter, priorityFilter]);

  const total   = visible.length;
  const done    = visible.filter(f => DONE_STATES.has(f.state)).length;
  const active  = visible.filter(f => ACTIVE_STATES.has(f.state)).length;
  const totalSP = visible.reduce((s, f) => s + (f.storyPoints || 0), 0);
  const avgCR   = total ? Math.round(visible.reduce((s, f) => s + (f.completionRate || 0), 0) / total) : 0;

  const stats = [
    { label: 'Features',     value: total,   color: PRIMARY },
    { label: 'Completed',    value: done,    color: '#2e7d32' },
    { label: 'In Progress',  value: active,  color: '#1565c0' },
    { label: 'Story Points', value: totalSP, color: '#795548' },
    { label: 'Avg Progress', value: `${avgCR}%`, color: crColor(avgCR) },
  ];

  return (
    <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
      {stats.map(s => (
        <Paper key={s.label} elevation={0} sx={{
          flex: '1 1 90px', borderRadius: '12px', px: 2, py: 1.5,
          border: '1.5px solid rgba(0,0,0,0.07)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25,
        }}>
          <Typography sx={{ fontSize: '1.35rem', fontWeight: 750, color: s.color, lineHeight: 1 }}>
            {s.value}
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: MUTED, fontWeight: 500 }}>{s.label}</Typography>
        </Paper>
      ))}
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const ReliabilityDeliveryView = () => {
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [features, setFeatures]         = useState([]);
  const [fixVersions, setFixVersions]   = useState([]);
  const [subCats, setSubCats]           = useState([]);
  const [selectedQuarter, setSelectedQuarter] = useState('all');
  const [fixVersionFilter, setFixVersionFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [deliveryRes, subCatRes] = await Promise.all([
        get('/api/reliability/delivery'),
        get('/api/reliability/subcategories'),
      ]);
      if (!deliveryRes.success) throw new Error(deliveryRes.message || 'Failed to load delivery data');
      setFeatures(deliveryRes.data || []);
      setFixVersions(deliveryRes.meta?.fixVersions || []);
      if (subCatRes.success) setSubCats(subCatRes.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // alias → name map for initiative labels
  const subCatMap = useMemo(() => {
    const m = {};
    (subCats || []).forEach(s => { m[s.alias] = s.name; });
    return m;
  }, [subCats]);

  // Features grouped by pillar
  const byPillar = useMemo(() => {
    const map = {};
    features.forEach(f => {
      if (!map[f.pillarKey]) map[f.pillarKey] = [];
      map[f.pillarKey].push(f);
    });
    return map;
  }, [features]);

  // Quarter summary counts (for the pill labels)
  const quarterCounts = useMemo(() => {
    const counts = { 'all': features.length };
    QUARTERS.slice(1).forEach(q => {
      counts[q.key] = features.filter(f =>
        (f.tags || []).some(t => t.toLowerCase() === q.key.toLowerCase())
      ).length;
    });
    return counts;
  }, [features]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress sx={{ color: PRIMARY }} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ borderRadius: '10px' }}>{error}</Alert>;
  }

  return (
    <Box>
      {/* Controls row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2.5, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Quarter toggle pills */}
        <Box sx={{ display: 'flex', gap: 0.75, p: 0.5, bgcolor: '#fff', borderRadius: '12px', border: '1.5px solid rgba(0,0,0,0.08)' }}>
          {QUARTERS.map(q => {
            const active = selectedQuarter === q.key;
            const accentColor = q.color || PRIMARY;
            return (
              <Box
                key={q.key}
                onClick={() => setSelectedQuarter(q.key)}
                sx={{
                  px: 1.75, py: 0.6,
                  borderRadius: '9px',
                  cursor: 'pointer',
                  bgcolor: active ? accentColor : 'transparent',
                  color: active ? '#fff' : MUTED,
                  fontWeight: active ? 700 : 500,
                  fontSize: '0.82rem',
                  transition: 'all 0.15s ease',
                  userSelect: 'none',
                  display: 'flex', alignItems: 'center', gap: 0.6,
                  '&:hover': { bgcolor: active ? accentColor : `${accentColor}15`, color: active ? '#fff' : accentColor },
                }}
              >
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 'inherit', color: 'inherit' }}>
                  {q.label}
                </Typography>
                <Box sx={{
                  px: 0.6, borderRadius: '4px',
                  bgcolor: active ? 'rgba(255,255,255,0.25)' : `${(q.color || PRIMARY)}18`,
                  display: 'flex', alignItems: 'center',
                }}>
                  <Typography sx={{ fontSize: '0.67rem', fontWeight: 700, color: active ? '#fff' : (q.color || PRIMARY) }}>
                    {quarterCounts[q.key] ?? 0}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Fix version filter */}
        {fixVersions.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel sx={{ fontSize: '0.82rem' }}>Fix Version</InputLabel>
            <Select
              value={fixVersionFilter}
              label="Fix Version"
              onChange={e => setFixVersionFilter(e.target.value)}
              sx={{ borderRadius: '10px', fontSize: '0.82rem' }}
            >
              <MenuItem value=""><em>All versions</em></MenuItem>
              {fixVersions.map(v => (
                <MenuItem key={v} value={v} sx={{ fontSize: '0.82rem' }}>{v}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Priority filter */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel sx={{ fontSize: '0.82rem' }}>Priority</InputLabel>
          <Select
            value={priorityFilter}
            label="Priority"
            onChange={e => setPriorityFilter(e.target.value)}
            sx={{ borderRadius: '10px', fontSize: '0.82rem' }}
          >
            <MenuItem value="all"><em>All priorities</em></MenuItem>
            {[1, 2, 3, 4].map(p => (
              <MenuItem key={p} value={String(p)} sx={{ fontSize: '0.82rem' }}>
                P{p}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Active filter indicator */}
        {(selectedQuarter !== 'all' || fixVersionFilter) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <FilterIcon sx={{ fontSize: 15, color: MUTED }} />
            <Typography sx={{ fontSize: '0.75rem', color: MUTED }}>
              {[
                selectedQuarter !== 'all' && `Quarter: ${QUARTERS.find(q => q.key === selectedQuarter)?.label}`,
                fixVersionFilter && `Version: ${fixVersionFilter}`,
              ].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Stats summary */}
      <StatBanner
        features={features}
        selectedQuarter={selectedQuarter}
        fixVersionFilter={fixVersionFilter}
        priorityFilter={priorityFilter}
      />

      {/* Pillar sections */}
      {Object.keys(PILLAR_META).map(pillarKey => (
        <PillarSection
          key={pillarKey}
          pillarKey={pillarKey}
          features={byPillar[pillarKey] || []}
          subCatMap={subCatMap}
          selectedQuarter={selectedQuarter}
          fixVersionFilter={fixVersionFilter}
          priorityFilter={priorityFilter}
        />
      ))}

      {features.length === 0 && (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: '14px', border: '1.5px solid rgba(0,0,0,0.08)' }}>
          <Typography variant="body2" color="text.secondary">
            No features found. Ensure the ADO epic IDs are configured and tags have been synced.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ReliabilityDeliveryView;
